import { Button, Dropdown, Spin, Tag, Modal, Avatar } from "antd";
import React, { useState } from "react";
import InfiniteTable from "../utils/InfiniteTable";
import {
  DeleteFilled,
  DownOutlined,
  EditFilled,
  EyeFilled,
  LockFilled,
  UnlockFilled,
} from "@ant-design/icons";
import { useUserContext } from "../../contexts/UserContext";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { DepartementService } from "../../services/DepartementService";
import { Link, useNavigate } from "react-router-dom";
import DepartementEditForm from "./DepartementEditForm";
import PropTypes from "prop-types";

const DepartementList = ({ paginationData, setPaginationData, departements, setDepartements, searchParams, init }) => {
  const { check } = useUserContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedDepartement, setSelectedDepartement] = useState(null);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  React.useEffect(() => {
    const getKeysToExpand = (items) => {
      let keys = [];

      const traverse = (item) => {
        if (item.children && item.children.length > 0) {
          keys.push(item.uid);
          item.children.forEach(child => traverse(child));
        }
      };

      items.forEach(item => traverse(item));
      return keys;
    };

    const keysToExpand = getKeysToExpand(departements);

    setExpandedRowKeys(keysToExpand);
  }, [departements]);

  const loadMore = () => {
    if (paginationData?.next_page_url && !loadingMore) {
      setLoadingMore(true);
      DepartementService
        .post(paginationData.next_page_url, searchParams)
        .then((response) => {
          if (!response) {
            return;
          }
          setPaginationData(response.data);
          setDepartements(departements => [...departements, ...response.data.data.filter(item => !departements.find(u => u.uid === item.uid))]);
        })
        .catch((error) => {
          catchError(error);
        })
        .finally(() => {
          setLoadingMore(false);
        });
    }
  };

  const handleDelete = (idOrUid) => {
    setLoading(true);
    DepartementService.getInstance()
      .remove(idOrUid)
      .then((response) => {
        Toast.success(response.message);
        init();
      })
      .catch((error) => {
        catchError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openEditModal = (item) => {
    setSelectedDepartement(item);
    setUpdateModalVisible(true);
  };

  const handleEnable = (departement) => {
    DepartementService.getInstance()
      .enable(departement.uid)
      .then((response) => {
        Toast.success(response.message);
        setDepartements(
          (departements) => departements.map((u) =>
            u.uid === response.data.uid ? response.data : u
          )
        );
      })
      .catch((error) => {
        Toast.error(error.message);
      });
  };

  const handleDisable = (departement) => {
    DepartementService.getInstance()
      .disable(departement.uid)
      .then((response) => {
        Toast.success(response.message);
        setDepartements(
          departements => departements.map((u) =>
            u.uid === response.data.uid ? response.data : u
          )
        );
      })
      .catch((error) => {
        Toast.error(error.message);
      });
  };

  const handleUpdate = (item) => {
    setDepartements(
      departements => departements.map((u) => (u.uid === item.uid ? item : u))
    );
    setSelectedDepartement(null);
    setUpdateModalVisible(false);
  };

  const gotoDepartementDetails = (item) => {
    navigate(`/departement/${item.uid}`);
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      width: 350,
      render: (nom, record)=> check('VIEW-DEPARTEMENT') ? <Link to={`/departement/${record.uid}`}>{nom}</Link> : nom,
      ellipsis: true
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 150,
    },
    {
      title: "Statut",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) => (
        enabled ? <Tag color="success">Actif</Tag> : <Tag color="error">Inactif</Tag>
      ),
      width: 150,
    },
    {
      title: "Responsable",
      dataIndex: ["responsable"],
      key: "personnel_id",
      render: (personnel) => personnel ? <>
      <Avatar src={personnel?.avatar_link} /> {personnel?.name}
      </> : "Non renseigné",
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'Voir',
                icon: <EyeFilled />,
                onClick: () => gotoDepartementDetails(record)
              },
              {
                key: 'edit',
                label: 'Modifier',
                icon: <EditFilled />,
                onClick: () => openEditModal(record)
              },
              {
                key: 'toggle',
                label: record.enabled ? 'Désactiver' : 'Activer',
                icon: record.enabled ? <LockFilled /> : <UnlockFilled />,
                onClick: () => {
                  Modal.confirm({
                    title: `Voulez-vous ${record.enabled ? 'désactiver' : 'activer'} cet departement?`,
                    okText: record.enabled ? 'Désactiver' : 'Activer',
                    cancelText: 'Annuler',
                    okType: record.enabled ? 'danger' : 'primary',
                    onOk: () => record.enabled ? handleDisable(record) : handleEnable(record)
                  });
                }
              },
              {
                key: 'delete',
                label: 'Supprimer',
                icon: <DeleteFilled />,
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: `Voulez-vous vraiment procéder à la suppression ?`,
                    okText: 'Confirmer',
                    cancelText: 'Annuler',
                    okType: 'danger',
                    onOk: () => handleDelete(record.uid)
                  });
                }
              }
            ].filter(item => {
              if (item.key === 'view') return check('VIEW-DEPARTEMENT');
              if (item.key === 'edit') return check('EDIT-DEPARTEMENT');
              if (item.key === 'delete') return check('DELETE-DEPARTEMENT');
              return true;
            })
          }}
        >
          <Button>
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
      width: 120,
      fixed: 'right'
    },

  ];

  return (
    <>
      <Spin spinning={loading}>
        <InfiniteTable  expandable={{
          expandedRowKeys: expandedRowKeys,  // Déploiement par défaut
          expandRowByClick: true,  // Développer la ligne par clic
          rowExpandable: (record) => record.children && record.children.length > 0,
        }} hideRowNumber
          rowKey={"uid"}
          dataSource={departements}
          paginationData={paginationData}
          loadMore={loadMore}
          loading={loadingMore}
          columns={columns}
        />
      </Spin>
      {selectedDepartement && (
        <DepartementEditForm
          visible={updateModalVisible}
          uid={selectedDepartement.uid}
          onUpdate={handleUpdate}
          onCancel={() => setUpdateModalVisible(false)}
        />
      )}
    </>
  );
}
export default DepartementList;

DepartementList.propTypes = {
  paginationData: PropTypes.object.isRequired,
  searchParams: PropTypes.object,
  setPaginationData: PropTypes.func.isRequired,
  departements: PropTypes.array.isRequired,
  setDepartements: PropTypes.func.isRequired,
  init: PropTypes.func.isRequired
};